import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import type { DispatchPlace } from '@/features/dispatch-places/dispatch-places.types';

import { AmazonPointPanel } from './sections/amazon-point-panel';
import { HistorySection } from './sections/history-section/history-section';
import { UsersSection } from './sections/users-section/users-section';
import { useDispatchPlaceDetailPage } from './use-dispatch-place-detail-page';
import type { DetailTab } from './use-dispatch-place-detail-page';

// ─── Label maps ───────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<DispatchPlace['type'], string> = {
  physical: 'Físico',
  locker: 'Locker',
  virtual: 'Virtual',
};

const STATUS_BADGE_CLASS: Record<DispatchPlace['status'], string> = {
  active: 'badge-success',
  inactive: 'badge-error',
  pending: 'badge-warning',
};

const STATUS_LABELS: Record<DispatchPlace['status'], string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  pending: 'Pendiente',
};

// ─── Tab definitions ──────────────────────────────────────────────────────────

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'general', label: 'Datos generales' },
  { id: 'usuarios', label: 'Usuarios' },
  { id: 'horarios', label: 'Horarios' },
  { id: 'facturacion', label: 'Datos de facturación' },
  { id: 'historial', label: 'Historial' },
];

// ─── InfoRow helper ───────────────────────────────────────────────────────────

interface InfoRowProps {
  label: string;
  value: string | number | null | undefined;
  monospace?: boolean;
}

function InfoRow(props: InfoRowProps) {
  const displayValue =
    props.value !== null && props.value !== undefined ? String(props.value) : '—';

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-base-content/60 text-xs">{props.label}</span>
      <span className={twMerge('text-sm font-semibold', props.monospace && 'font-mono')}>
        {displayValue}
      </span>
    </div>
  );
}

// ─── Section card wrapper ─────────────────────────────────────────────────────

interface SectionCardProps {
  title: string;
  children: ReactNode;
}

function SectionCard(props: SectionCardProps) {
  return (
    <div className="card card-border border-base-300 bg-base-100">
      <div className="card-body gap-4">
        <h2 className="card-title text-base">{props.title}</h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">{props.children}</div>
      </div>
    </div>
  );
}

// ─── Tab content panels ───────────────────────────────────────────────────────

interface GeneralTabProps {
  place: DispatchPlace;
}

function GeneralTab(props: GeneralTabProps) {
  const place = props.place;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <SectionCard title="Información general">
        <InfoRow label="Tipo" value={TYPE_LABELS[place.type]} />
        <InfoRow label="País" value={place.country_code.toUpperCase()} />
        <InfoRow label="Capacidad" value={place.capacity} />
        <div className="col-span-2">
          <InfoRow label="Descripción" value={place.description} />
        </div>
      </SectionCard>

      <SectionCard title="Proveedor">
        <InfoRow label="ID" value={place.provider?.id} />
        <InfoRow label="Nombre" value={place.provider?.name} />
      </SectionCard>

      <SectionCard title="Dirección">
        <div className="col-span-2">
          <InfoRow
            label="Calle y número"
            value={
              place.address
                ? `${place.address.street} ${place.address.number}`
                : null
            }
          />
        </div>
        <InfoRow label="Colonia" value={place.address?.neighborhood} />
        <InfoRow label="Ciudad" value={place.address?.city} />
        <InfoRow label="Estado" value={place.address?.state} />
        <InfoRow label="C.P." value={place.address?.postal_code} />
        <InfoRow label="País" value={place.address?.country} />
      </SectionCard>

      <SectionCard title="Contacto">
        <InfoRow label="Nombre" value={place.contact?.name} />
        <InfoRow label="Teléfono" value={place.contact?.phone} />
        <div className="col-span-2">
          <InfoRow label="Correo electrónico" value={place.contact?.email} />
        </div>
      </SectionCard>

      <SectionCard title="Ubicación">
        <InfoRow label="Latitud" value={place.location?.coordinates[1]} monospace />
        <InfoRow label="Longitud" value={place.location?.coordinates[0]} monospace />
      </SectionCard>

      <AmazonPointPanel place={place} />
    </div>
  );
}

interface FacturacionTabProps {
  place: DispatchPlace;
}

function FacturacionTab(props: FacturacionTabProps) {
  const place = props.place;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <SectionCard title="Facturación">
        <InfoRow
          label="Tipo de ID fiscal"
          value={place.billing?.tax_id_type?.toUpperCase()}
        />
        <InfoRow label="Valor de ID fiscal" value={place.billing?.tax_id_value} />
        <InfoRow label="Razón social" value={place.billing?.legal_name} />
        <InfoRow label="Régimen" value={place.billing?.regime} />
      </SectionCard>

      <SectionCard title="Datos bancarios">
        <InfoRow
          label="Tipo de cuenta"
          value={place.banking?.account_id_type?.toUpperCase()}
        />
        <InfoRow label="Número de cuenta" value={place.banking?.account_id_value} />
        <InfoRow label="Banco" value={place.banking?.bank_name} />
        <InfoRow label="Titular" value={place.banking?.holder_name} />
      </SectionCard>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DispatchPlaceDetailPage() {
  const { place, isLoading, isError, active_tab, onTabChange } =
    useDispatchPlaceDetailPage();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="alert alert-error">
        <span>Ocurrió un error al cargar el punto de despacho. Intenta de nuevo.</span>
      </div>
    );
  }

  if (!place) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <p className="text-base-content/60">No se encontró el punto de despacho.</p>
        <Link to="/dispatch-places" className="btn btn-ghost btn-sm gap-1">
          <ArrowLeft size={16} />
          Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <header className="flex flex-wrap items-center gap-3">
        <Link
          to="/dispatch-places"
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Volver al listado"
        >
          <ArrowLeft size={18} />
        </Link>

        <h1 className="flex-1 text-2xl font-bold">{place.name}</h1>

        <span className={twMerge('badge badge-lg', STATUS_BADGE_CLASS[place.status])}>
          {STATUS_LABELS[place.status]}
        </span>
      </header>

      {/* Tab navigation */}
      <div role="tablist" className="tabs tabs-border overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={active_tab === tab.id}
            className={twMerge('tab whitespace-nowrap', active_tab === tab.id && 'tab-active')}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      {active_tab === 'general' && <GeneralTab place={place} />}

      {active_tab === 'usuarios' && <UsersSection dp_id={place.id} />}

      {active_tab === 'horarios' && (
        <div className="card card-border border-base-300 bg-base-100">
          <div className="card-body items-center py-12 text-base-content/40">
            <p className="text-sm">Horarios próximamente.</p>
          </div>
        </div>
      )}

      {active_tab === 'facturacion' && <FacturacionTab place={place} />}

      {active_tab === 'historial' && (
        <div className="card card-border border-base-300 bg-base-100">
          <div className="card-body gap-6">
            <h2 className="card-title text-base">Historial de cambios</h2>
            <HistorySection dp_id={place.id} />
          </div>
        </div>
      )}
    </div>
  );
}
